import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { api, apiPatients, genderOptions, patientsBaseRoute, IPatient } from '../../types';
import { createApiHeaders } from '../../utilities/JwtTokenParser';
import Header from '../../components/Header';
import Loader from '../../components/Loader';
import { useAlert } from '../../layout/MainAlert';

const patientPrototype : IPatient = {
  id: 0, 
  personalHealthNumber: "",
  firstName: "",
  lastName: "",
  gender: "",
  dateOfBirth: new Date(),
  addressLine1: "",
  addressLine2: "",
  city: "",
  province: "",
  postal: "",
  email: "",
  homePhone: "",
  mobilePhone: "",
  workPhone: "",
  dateOfFirstPrescription: new Date(),
  dateOfLastPrescription: new Date(),
  showUpFrequency: ""
}

const fetchPatientData = async (patientId: string): Promise<IPatient> => {
  const data = await fetch(`${api}${apiPatients}${patientId}`, {
    method: 'GET', 
    headers: createApiHeaders(true, true)
  });
  
  const json = await data.json();
  return json;
}

const savePatientData = async (patient: IPatient): Promise<IPatient> => {
  const response = await fetch(`${api}${apiPatients}${patient.id}`, {
    method: 'PUT',
    headers: createApiHeaders(true, true),
    body: JSON.stringify({
      ...patient, 
      dateOfBirth: dayjs(patient.dateOfBirth).format('YYYY-MM-DD') 
    })
  });

  if (response.ok) {
    const json = await response.json();
    return json;
  }

  throw new Error(`Detailed Error Code: ${response.status}.`);
}

const validationSchema = yup.object().shape({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required")
});

const PatientEdit = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const params = useParams();
  const alert = useAlert();

  const patientId = params.id ?? '0';
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [patient, setPatient] = useState<IPatient>(patientPrototype);

  useEffect(() => {
    setIsLoading(true);
    fetchPatientData(patientId)
      .then((response) => { 
        setPatient(response as IPatient);
        setIsLoading(false);
        setIsSuccess(true); 
      })
      .catch((reason) => {
        console.error(reason);
        setIsLoading(false);
        setIsSuccess(false);
      }); 
  }, []);

  const formik = useFormik({
    initialValues: {
      ...patient, 
      dateOfBirth: dayjs(patient.dateOfBirth) 
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      savePatientData({
        ...values,
        dateOfBirth: values.dateOfBirth.toDate()
      })
      .then((response) => {
        alert.success(`Patient "${response.lastName}, ${response.firstName}" saved successfully.`);
        navigate(`${patientsBaseRoute}`);
      })
      .catch((error) => {
        alert.error(`Patient can't be saved. ${error}`);
      });
    }
  });
  
  return (
    <Box m="14px">
      <Box 
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gap="20px" 
        sx={{
          "& > div": { gridColumn: isNonMobile ? undefined : "span 12" }
        }}
      >
        <Box 
          display="flex"
          sx={{
            gridColumn: 'span 4'
          }}
        >
          <Header title="PATIENT INFO" subtitle="" />
        </Box>

        <Box
          display="flex" 
          justifyContent="center"
          sx={{
            gridColumn: 'span 4'
          }}
        >
          <Loader 
            isLoading={isLoading} 
            isSuccess={isSuccess} 
            loadingText="LOADING PATIENT INFO..."
            successText="GREAT! PATIENT INFO LOADED."
          />
        </Box>
      </Box>

      <form onSubmit={formik.handleSubmit}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(6, minmax(0, 1fr))"
          gap="20px"
          sx={{
            "& > div": { gridColumn: isNonMobile ? undefined : "span 6" }
          }}
        >
          <TextField
            fullWidth
            variant="filled"
            id="firstName"
            name="firstName"
            label="First Name"
            color="info"
            value={formik.values.firstName}
            onChange={formik.handleChange} 
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
            sx={{ gridColumn: "span 3" }}
          />
              
          <TextField
            fullWidth
            variant="filled"
            id="lastName"
            name="lastName"
            label="Last Name"
            color="info"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
            sx={{ gridColumn: "span 3" }}
          />

          <TextField
            fullWidth
            variant="filled"
            id="personalHealthNumber"
            name="personalHealthNumber"
            label="Health Number"
            color="info"
            value={formik.values.personalHealthNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.personalHealthNumber && Boolean(formik.errors.personalHealthNumber)}
            helperText={formik.touched.personalHealthNumber && formik.errors.personalHealthNumber}
            sx={{ gridColumn: "span 2" }}
          />

          <TextField
            select
            fullWidth
            variant="filled"
            id="gender"
            name="gender"
            label="Gender"
            color="info"
            value={formik.values.gender}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.gender && Boolean(formik.errors.gender)}
            helperText={formik.touched.gender && formik.errors.gender}
            sx={{ gridColumn: "span 2" }}
          >
            {genderOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              name="dateOfBirth"
              label="Date of Birth"
              value={formik.values.dateOfBirth}
              onChange={(value) => {
                setPatient({
                  ...formik.values, 
                  dateOfBirth: value?.toDate() ?? formik.values.dateOfBirth.toDate()
                });
              }}
              slotProps={{ 
                textField: { 
                  fullWidth: true,
                  variant: "filled",
                  color: "info",
                  error: formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth)
                }
              }}
              sx={{ gridColumn: "span 2" }}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            variant="filled"
            id="addressLine1"
            name="addressLine1"
            label="Address Line 1"
            color="info"
            value={formik.values.addressLine1}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.addressLine1 && Boolean(formik.errors.addressLine1)}
            helperText={formik.touched.addressLine1 && formik.errors.addressLine1}
            sx={{ gridColumn: "span 6" }}
          />

          <TextField
            fullWidth
            variant="filled"
            id="addressLine2"
            name="addressLine2"
            label="Address Line 2"
            color="info"
            value={formik.values.addressLine2}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.addressLine2 && Boolean(formik.errors.addressLine2)}
            helperText={formik.touched.addressLine2 && formik.errors.addressLine2}
            sx={{ gridColumn: "span 6" }}
          />

          <TextField
            fullWidth
            variant="filled"
            id="city"
            name="city"
            label="City"
            color="info"
            value={formik.values.city}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.city && Boolean(formik.errors.city)}
            helperText={formik.touched.city && formik.errors.city}
            sx={{ gridColumn: "span 2" }}
          />

          <TextField
            fullWidth
            variant="filled"
            id="province"
            name="province"
            label="Province"
            color="info"
            value={formik.values.province}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.province && Boolean(formik.errors.province)}
            helperText={formik.touched.province && formik.errors.province}
            sx={{ gridColumn: "span 2" }}
          />

          <TextField
            fullWidth
            variant="filled"
            id="postal"
            name="postal"
            label="Postal"
            color="info"
            value={formik.values.postal}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.postal && Boolean(formik.errors.postal)}
            helperText={formik.touched.postal && formik.errors.postal}
            sx={{ gridColumn: "span 2" }}
          />

          <TextField 
            fullWidth
            variant="filled"
            id="mobilePhone"
            name="mobilePhone"
            label="Mobile Phone"
            color="info"
            value={formik.values.mobilePhone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.mobilePhone && Boolean(formik.errors.mobilePhone)}
            helperText={formik.touched.mobilePhone && formik.errors.mobilePhone}
            sx={{ gridColumn: "span 2" }}
          />

          <TextField 
            fullWidth
            variant="filled"
            id="homePhone"
            name="homePhone"
            label="Home Phone"
            color="info"
            value={formik.values.homePhone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.homePhone && Boolean(formik.errors.homePhone)}
            helperText={formik.touched.homePhone && formik.errors.homePhone}
            sx={{ gridColumn: "span 2" }}
          />

          <TextField 
            fullWidth
            variant="filled"
            id="workPhone"
            name="workPhone"
            label="Work Phone"
            color="info"
            value={formik.values.workPhone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.workPhone && Boolean(formik.errors.workPhone)}
            helperText={formik.touched.workPhone && formik.errors.workPhone}
            sx={{ gridColumn: "span 2" }}
          />
        </Box>
            
        <Box display="flex" justifyContent="end" mt="20px">
          <Button 
            type="submit" 
            variant="contained" 
            color="secondary"
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              padding: "8px 14px",
            }}
          >
            Save Changes
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default PatientEdit;